import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";

import { PwaInstallPrompt } from "./components/PwaInstallPrompt";
import { AppShell } from "./components/layout/AppShell";
import { DealsPage } from "./features/deals/DealsPage";
import { normalizeInternalAppPath } from "./lib/deep-links";
import { useAuth } from "./state/auth-store";

const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const ContactsPage = lazy(() => import("./pages/ContactsPage"));
const TasksPage = lazy(() => import("./pages/TasksPage"));
const MailPage = lazy(() => import("./pages/MailPage"));
const ActivityPage = lazy(() => import("./pages/ActivityPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const AcceptInvitationPage = lazy(() => import("./pages/AcceptInvitationPage"));

const loadingFallback = <div className="route-loading" role="status">Загружаем рабочее пространство…</div>;

export default function App() {
  const { status } = useAuth();
  return (
    <>
      <Suspense fallback={loadingFallback}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
          <Route element={<AuthGate />}>
            <Route element={<AppShell />}>
              <Route index element={<DashboardPage />} />
              <Route path="deals" element={<DealsPage />} />
              <Route path="contacts" element={<ContactsPage />} />
              <Route path="tasks" element={<TasksPage />} />
              <Route path="mail" element={<MailPage />} />
              <Route path="activity" element={<ActivityPage />} />
              <Route path="settings" element={<AdminOnly><SettingsPage /></AdminOnly>} />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <PwaInstallPrompt enabled={status === "authenticated"} />
    </>
  );
}

function AuthGate() {
  const { status } = useAuth();
  const location = useLocation();
  if (status === "loading") return loadingFallback;
  if (status === "anonymous") {
    const from = normalizeInternalAppPath(`${location.pathname}${location.search}`) ?? "/deals";
    return <Navigate to="/login" replace state={{ from }} />;
  }
  return <Outlet />;
}

function AdminOnly({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  if (session?.user.role !== "owner" && session?.user.role !== "admin") return <Navigate to="/deals" replace />;
  return children;
}
